// frontend/src/components/common/MathFormulaRenderer.tsx
// Matematik formülleri (KaTeX / LaTeX), SVG grafik ve şekil diyagramları ile zenginleştirilmiş görsel soru render bileşeni.

import React, { useMemo } from 'react';
import katex from 'katex';
import DOMPurify from 'dompurify';
import 'katex/dist/katex.min.css';

interface MathFormulaRendererProps {
  text: string;
  className?: string;
  isBlock?: boolean;
}

/**
 * Ham ASCII matematik ifadelerini (örn. integral(f(x)^2, 0, 4), sqrt(x), e^(x/2), pi)
 * düzgün LaTeX formatına dönüştürür.
 */
function autoFormatAsciiMath(raw: string): string {
  let s = raw;

  // Zaten $ işaretleri varsa müdahale etme
  if (s.includes('$') || s.includes('\\int') || s.includes('\\frac')) {
    return s;
  }

  // integral(expr, a, b) -> \int_{a}^{b} expr \, dx
  s = s.replace(/integral\s*\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi, '$\\int_{$2}^{$3} $1 \\, dx$');
  
  // integral(expr) -> \int expr \, dx
  s = s.replace(/integral\s*\(\s*([^)]+)\s*\)/gi, '$\\int $1 \\, dx$');

  // sqrt(expr) -> \sqrt{expr}
  s = s.replace(/sqrt\s*\(\s*([^)]+)\s*\)/gi, '$\\sqrt{$1}$');

  // e^(expr) -> e^{expr}
  s = s.replace(/e\s*\^\s*\(\s*([^)]+)\s*\)/gi, '$e^{$1}$');

  // e^x -> e^x
  s = s.replace(/e\^([0-9a-zA-Z]+)/g, '$e^{$1}$');

  // pi -> \pi
  s = s.replace(/\bpi\b/g, '$\\pi$');

  // f'(x) -> f'(x)
  s = s.replace(/\bf'\(([a-zA-Z0-9]+)\)/g, '$f\'($1)$');

  // f(x) -> f(x)
  s = s.replace(/\bf\(([a-zA-Z0-9]+)\)/g, '$f($1)$');

  return s;
}

export function MathFormulaRenderer({ text, className = '', isBlock = false }: MathFormulaRendererProps) {
  const renderedContent = useMemo(() => {
    if (!text) return null;

    // 1. Önce SVG etiketlerini ayır
    const svgRegex = /(<svg[\s\S]*?<\/svg>)/gi;
    const partsBySvg = text.split(svgRegex);

    return partsBySvg.map((part, svgIdx) => {
      // Eğer bu parça bir SVG ise doğrudan güvenli şekilde sanitize edip SVG render et
      if (part.trim().toLowerCase().startsWith('<svg') && part.trim().toLowerCase().endsWith('</svg>')) {
        const cleanSvg = DOMPurify.sanitize(part, {
          USE_PROFILES: { svg: true, svgFilters: true },
        });
        return (
          <div
            key={`svg-${svgIdx}`}
            className="math-diagram-container"
            dangerouslySetInnerHTML={{ __html: cleanSvg }}
          />
        );
      }

      // Kod bloklarını (```...```) ayır
      const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
      const partsByCode = part.split(codeBlockRegex);

      return partsByCode.map((codePart, codeIdx) => {
        if (codeIdx % 2 === 1) {
          // Kod bloğu
          return (
            <pre key={`code-${svgIdx}-${codeIdx}`} className="quiz-question-code">
              <code>{codePart}</code>
            </pre>
          );
        }

        // Metin ve Matematik Formülleri ($...$ veya $$...$$)
        const formattedText = autoFormatAsciiMath(codePart);

        // $$...$$ (Display math) ve $...$ (Inline math) parse et
        // Regex: $$...$$ veya $...$
        const mathRegex = /(\$\$[\s\S]*?\$\$|\$[^\$\n]+?\$)/g;
        const subParts = formattedText.split(mathRegex);

        return (
          <span key={`text-${svgIdx}-${codeIdx}`}>
            {subParts.map((sub, mathIdx) => {
              if (sub.startsWith('$$') && sub.endsWith('$$')) {
                const formula = sub.slice(2, -2).trim();
                try {
                  const html = katex.renderToString(formula, {
                    displayMode: true,
                    throwOnError: false,
                  });
                  return (
                    <div
                      key={`math-block-${mathIdx}`}
                      className="katex-display-box"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  );
                } catch {
                  return <div key={`math-err-${mathIdx}`} className="katex-error">{sub}</div>;
                }
              } else if (sub.startsWith('$') && sub.endsWith('$')) {
                const formula = sub.slice(1, -1).trim();
                try {
                  const html = katex.renderToString(formula, {
                    displayMode: false,
                    throwOnError: false,
                  });
                  return (
                    <span
                      key={`math-inline-${mathIdx}`}
                      className="katex-inline-box"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  );
                } catch {
                  return <span key={`math-err-${mathIdx}`} className="katex-error">{sub}</span>;
                }
              }

              return <span key={`raw-${mathIdx}`}>{sub}</span>;
            })}
          </span>
        );
      });
    });
  }, [text]);

  const Tag = isBlock ? 'div' : 'span';

  return <Tag className={`math-rendered-text ${className}`}>{renderedContent}</Tag>;
}
